import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Fetch budget records
  isFetchingBudgetRecords: false,
  budgetRecords: [],
  fetchBudgetRecordsError: null,

  // Fetch resource configs
  isFetchingResourceConfigs: false,
  resourceConfigs: null,
  fetchResourceConfigsError: null,

  // Create budget record
  isCreatingBudgetRecord: false,
  createBudgetRecordSuccess: null,
  createBudgetRecordError: null,

  // Update budget record
  isUpdatingBudgetRecord: false,
  updateBudgetRecordSuccess: null,
  updateBudgetRecordError: null,

  // Delete budget record
  isDeletingBudgetRecord: false,
  deleteBudgetRecordSuccess: null,
  deleteBudgetRecordError: null,

  // Organization Budget Management
  isFetchingOrgBudgets: false,
  orgBudgets: [],
  fetchOrgBudgetsError: null,

  isCreatingOrgBudget: false,
  createOrgBudgetSuccess: null,
  createOrgBudgetError: null,

  isUpdatingOrgBudget: false,
  updateOrgBudgetSuccess: null,
  updateOrgBudgetError: null,

  isDeletingOrgBudget: false,
  deleteOrgBudgetSuccess: null,
  deleteOrgBudgetError: null,

  // Budget Charts
  isFetchingBudgetCharts: false,
  budgetCharts: null,
  fetchBudgetChartsError: null,

  // Budget History
  isFetchingBudgetHistory: false,
  budgetHistory: null,
  fetchBudgetHistoryError: null,

  // Organization Budget History
  isFetchingOrgBudgetHistory: false,
  orgBudgetHistory: null,
  fetchOrgBudgetHistoryError: null,

  // Organization Budget Alerts
  isFetchingOrgBudgetAlerts: false,
  orgBudgetAlerts: [],
  fetchOrgBudgetAlertsError: null,

  isCreatingOrgBudgetAlert: false,
  createOrgBudgetAlertSuccess: null,
  createOrgBudgetAlertError: null,

  isUpdatingOrgBudgetAlert: false,
  updateOrgBudgetAlertSuccess: null,
  updateOrgBudgetAlertError: null,

  isDeletingOrgBudgetAlert: false,
  deleteOrgBudgetAlertSuccess: null,
  deleteOrgBudgetAlertError: null,

  // Organization Budget Scheduled Reports
  isFetchingOrgBudgetScheduledReports: false,
  orgBudgetScheduledReports: [],
  fetchOrgBudgetScheduledReportsError: null,

  isCreatingOrgBudgetScheduledReport: false,
  createOrgBudgetScheduledReportSuccess: null,
  createOrgBudgetScheduledReportError: null,

  isUpdatingOrgBudgetScheduledReport: false,
  updateOrgBudgetScheduledReportSuccess: null,
  updateOrgBudgetScheduledReportError: null,

  isDeletingOrgBudgetScheduledReport: false,
  deleteOrgBudgetScheduledReportSuccess: null,
  deleteOrgBudgetScheduledReportError: null,

  // Budget Group Alerts
  isFetchingBudgetGroupAlerts: false,
  budgetGroupAlerts: [],
  fetchBudgetGroupAlertsError: null,

  isCreatingBudgetGroupAlert: false,
  createBudgetGroupAlertSuccess: null,
  createBudgetGroupAlertError: null,

  isUpdatingBudgetGroupAlert: false,
  updateBudgetGroupAlertSuccess: null,
  updateBudgetGroupAlertError: null,

  isDeletingBudgetGroupAlert: false,
  deleteBudgetGroupAlertSuccess: null,
  deleteBudgetGroupAlertError: null,

  // Budget Group Scheduled Reports
  isFetchingBudgetGroupScheduledReports: false,
  budgetGroupScheduledReports: [],
  fetchBudgetGroupScheduledReportsError: null,

  isCreatingBudgetGroupScheduledReport: false,
  createBudgetGroupScheduledReportSuccess: null,
  createBudgetGroupScheduledReportError: null,

  isUpdatingBudgetGroupScheduledReport: false,
  updateBudgetGroupScheduledReportSuccess: null,
  updateBudgetGroupScheduledReportError: null,

  isDeletingBudgetGroupScheduledReport: false,
  deleteBudgetGroupScheduledReportSuccess: null,
  deleteBudgetGroupScheduledReportError: null,
};

const budgetSlice = createSlice({
  name: "budget",
  initialState,
  reducers: {
    // Fetch budget records
    getBudgetRecords: (state) => {
      state.isFetchingBudgetRecords = true;
      state.fetchBudgetRecordsError = null;
    },
    getBudgetRecordsSuccess: (state, action) => {
      state.isFetchingBudgetRecords = false;
      state.budgetRecords = action.payload;
      state.fetchBudgetRecordsError = null;
    },
    getBudgetRecordsFailure: (state, action) => {
      state.isFetchingBudgetRecords = false;
      state.budgetRecords = [];
      state.fetchBudgetRecordsError = action.payload;
    },

    // Fetch resource configs
    getResourceConfigs: (state) => {
      state.isFetchingResourceConfigs = true;
      state.fetchResourceConfigsError = null;
    },
    getResourceConfigsSuccess: (state, action) => {
      state.isFetchingResourceConfigs = false;
      state.resourceConfigs = action.payload;
      state.fetchResourceConfigsError = null;
    },
    getResourceConfigsFailure: (state, action) => {
      state.isFetchingResourceConfigs = false;
      state.resourceConfigs = null;
      state.fetchResourceConfigsError = action.payload;
    },

    // Create budget record
    createBudgetRecord: (state) => {
      state.isCreatingBudgetRecord = true;
      state.createBudgetRecordSuccess = null;
      state.createBudgetRecordError = null;
    },
    createBudgetRecordSuccess: (state, action) => {
      state.isCreatingBudgetRecord = false;
      state.createBudgetRecordSuccess = action.payload;
      state.createBudgetRecordError = null;
    },
    createBudgetRecordFailure: (state, action) => {
      state.isCreatingBudgetRecord = false;
      state.createBudgetRecordError = action.payload;
    },

    // Update budget record
    updateBudgetRecord: (state) => {
      state.isUpdatingBudgetRecord = true;
      state.updateBudgetRecordSuccess = null;
      state.updateBudgetRecordError = null;
    },
    updateBudgetRecordSuccess: (state, action) => {
      state.isUpdatingBudgetRecord = false;
      state.updateBudgetRecordSuccess = action.payload;
      state.updateBudgetRecordError = null;
    },
    updateBudgetRecordFailure: (state, action) => {
      state.isUpdatingBudgetRecord = false;
      state.updateBudgetRecordError = action.payload;
    },

    // Delete budget record
    deleteBudgetRecord: (state) => {
      state.isDeletingBudgetRecord = true;
      state.deleteBudgetRecordSuccess = null;
      state.deleteBudgetRecordError = null;
    },
    deleteBudgetRecordSuccess: (state, action) => {
      state.isDeletingBudgetRecord = false;
      state.deleteBudgetRecordSuccess = action.payload;
      state.deleteBudgetRecordError = null;
    },
    deleteBudgetRecordFailure: (state, action) => {
      state.isDeletingBudgetRecord = false;
      state.deleteBudgetRecordError = action.payload;
    },

    // Organization Budget Management
    getOrgBudgets: (state) => {
      state.isFetchingOrgBudgets = true;
      state.fetchOrgBudgetsError = null;
    },
    getOrgBudgetsSuccess: (state, action) => {
      state.isFetchingOrgBudgets = false;
      state.orgBudgets = action.payload;
      state.fetchOrgBudgetsError = null;
    },
    getOrgBudgetsFailure: (state, action) => {
      state.isFetchingOrgBudgets = false;
      state.orgBudgets = [];
      state.fetchOrgBudgetsError = action.payload;
    },

    createOrgBudget: (state) => {
      state.isCreatingOrgBudget = true;
      state.createOrgBudgetSuccess = null;
      state.createOrgBudgetError = null;
    },
    createOrgBudgetSuccess: (state, action) => {
      state.isCreatingOrgBudget = false;
      state.createOrgBudgetSuccess = action.payload;
      state.createOrgBudgetError = null;
    },
    createOrgBudgetFailure: (state, action) => {
      state.isCreatingOrgBudget = false;
      state.createOrgBudgetError = action.payload;
    },

    updateOrgBudget: (state) => {
      state.isUpdatingOrgBudget = true;
      state.updateOrgBudgetSuccess = null;
      state.updateOrgBudgetError = null;
    },
    updateOrgBudgetSuccess: (state, action) => {
      state.isUpdatingOrgBudget = false;
      state.updateOrgBudgetSuccess = action.payload;
      state.updateOrgBudgetError = null;
    },
    updateOrgBudgetFailure: (state, action) => {
      state.isUpdatingOrgBudget = false;
      state.updateOrgBudgetError = action.payload;
    },

    deleteOrgBudget: (state) => {
      state.isDeletingOrgBudget = true;
      state.deleteOrgBudgetSuccess = null;
      state.deleteOrgBudgetError = null;
    },
    deleteOrgBudgetSuccess: (state, action) => {
      state.isDeletingOrgBudget = false;
      state.deleteOrgBudgetSuccess = action.payload;
      state.deleteOrgBudgetError = null;
    },
    deleteOrgBudgetFailure: (state, action) => {
      state.isDeletingOrgBudget = false;
      state.deleteOrgBudgetError = action.payload;
    },

    // Budget Charts
    getBudgetCharts: (state) => {
      state.isFetchingBudgetCharts = true;
      state.fetchBudgetChartsError = null;
    },
    getBudgetChartsSuccess: (state, action) => {
      state.isFetchingBudgetCharts = false;
      state.budgetCharts = action.payload;
      state.fetchBudgetChartsError = null;
    },
    getBudgetChartsFailure: (state, action) => {
      state.isFetchingBudgetCharts = false;
      state.budgetCharts = null;
      state.fetchBudgetChartsError = action.payload;
    },

    // Budget History
    getBudgetHistory: (state) => {
      state.isFetchingBudgetHistory = true;
      state.fetchBudgetHistoryError = null;
    },
    getBudgetHistorySuccess: (state, action) => {
      state.isFetchingBudgetHistory = false;
      state.budgetHistory = action.payload;
      state.fetchBudgetHistoryError = null;
    },
    getBudgetHistoryFailure: (state, action) => {
      state.isFetchingBudgetHistory = false;
      state.budgetHistory = null;
      state.fetchBudgetHistoryError = action.payload;
    },

    // Organization Budget History
    getOrgBudgetHistory: (state) => {
      state.isFetchingOrgBudgetHistory = true;
      state.fetchOrgBudgetHistoryError = null;
    },
    getOrgBudgetHistorySuccess: (state, action) => {
      state.isFetchingOrgBudgetHistory = false;
      state.orgBudgetHistory = action.payload;
      state.fetchOrgBudgetHistoryError = null;
    },
    getOrgBudgetHistoryFailure: (state, action) => {
      state.isFetchingOrgBudgetHistory = false;
      state.orgBudgetHistory = null;
      state.fetchOrgBudgetHistoryError = action.payload;
    },

    // Organization Budget Alerts
    getOrgBudgetAlerts: (state) => {
      state.isFetchingOrgBudgetAlerts = true;
      state.fetchOrgBudgetAlertsError = null;
    },
    getOrgBudgetAlertsSuccess: (state, action) => {
      state.isFetchingOrgBudgetAlerts = false;
      state.orgBudgetAlerts = action.payload;
      state.fetchOrgBudgetAlertsError = null;
    },
    getOrgBudgetAlertsFailure: (state, action) => {
      state.isFetchingOrgBudgetAlerts = false;
      state.orgBudgetAlerts = [];
      state.fetchOrgBudgetAlertsError = action.payload;
    },

    createOrgBudgetAlert: (state) => {
      state.isCreatingOrgBudgetAlert = true;
      state.createOrgBudgetAlertSuccess = null;
      state.createOrgBudgetAlertError = null;
    },
    createOrgBudgetAlertSuccess: (state, action) => {
      state.isCreatingOrgBudgetAlert = false;
      state.createOrgBudgetAlertSuccess = action.payload;
      state.createOrgBudgetAlertError = null;
    },
    createOrgBudgetAlertFailure: (state, action) => {
      state.isCreatingOrgBudgetAlert = false;
      state.createOrgBudgetAlertError = action.payload;
    },

    updateOrgBudgetAlert: (state) => {
      state.isUpdatingOrgBudgetAlert = true;
      state.updateOrgBudgetAlertSuccess = null;
      state.updateOrgBudgetAlertError = null;
    },
    updateOrgBudgetAlertSuccess: (state, action) => {
      state.isUpdatingOrgBudgetAlert = false;
      state.updateOrgBudgetAlertSuccess = action.payload;
      state.updateOrgBudgetAlertError = null;
    },
    updateOrgBudgetAlertFailure: (state, action) => {
      state.isUpdatingOrgBudgetAlert = false;
      state.updateOrgBudgetAlertError = action.payload;
    },

    deleteOrgBudgetAlert: (state) => {
      state.isDeletingOrgBudgetAlert = true;
      state.deleteOrgBudgetAlertSuccess = null;
      state.deleteOrgBudgetAlertError = null;
    },
    deleteOrgBudgetAlertSuccess: (state, action) => {
      state.isDeletingOrgBudgetAlert = false;
      state.deleteOrgBudgetAlertSuccess = action.payload;
      state.deleteOrgBudgetAlertError = null;
    },
    deleteOrgBudgetAlertFailure: (state, action) => {
      state.isDeletingOrgBudgetAlert = false;
      state.deleteOrgBudgetAlertError = action.payload;
    },

    // Organization Budget Scheduled Reports
    getOrgBudgetScheduledReports: (state) => {
      state.isFetchingOrgBudgetScheduledReports = true;
      state.fetchOrgBudgetScheduledReportsError = null;
    },
    getOrgBudgetScheduledReportsSuccess: (state, action) => {
      state.isFetchingOrgBudgetScheduledReports = false;
      state.orgBudgetScheduledReports = action.payload;
      state.fetchOrgBudgetScheduledReportsError = null;
    },
    getOrgBudgetScheduledReportsFailure: (state, action) => {
      state.isFetchingOrgBudgetScheduledReports = false;
      state.fetchOrgBudgetScheduledReportsError = action.payload;
    },

    createOrgBudgetScheduledReport: (state) => {
      state.isCreatingOrgBudgetScheduledReport = true;
      state.createOrgBudgetScheduledReportSuccess = null;
      state.createOrgBudgetScheduledReportError = null;
    },
    createOrgBudgetScheduledReportSuccess: (state, action) => {
      state.isCreatingOrgBudgetScheduledReport = false;
      state.createOrgBudgetScheduledReportSuccess = action.payload;
      state.createOrgBudgetScheduledReportError = null;
    },
    createOrgBudgetScheduledReportFailure: (state, action) => {
      state.isCreatingOrgBudgetScheduledReport = false;
      state.createOrgBudgetScheduledReportError = action.payload;
    },

    updateOrgBudgetScheduledReport: (state) => {
      state.isUpdatingOrgBudgetScheduledReport = true;
      state.updateOrgBudgetScheduledReportSuccess = null;
      state.updateOrgBudgetScheduledReportError = null;
    },
    updateOrgBudgetScheduledReportSuccess: (state, action) => {
      state.isUpdatingOrgBudgetScheduledReport = false;
      state.updateOrgBudgetScheduledReportSuccess = action.payload;
      state.updateOrgBudgetScheduledReportError = null;
    },
    updateOrgBudgetScheduledReportFailure: (state, action) => {
      state.isUpdatingOrgBudgetScheduledReport = false;
      state.updateOrgBudgetScheduledReportError = action.payload;
    },

    deleteOrgBudgetScheduledReport: (state) => {
      state.isDeletingOrgBudgetScheduledReport = true;
      state.deleteOrgBudgetScheduledReportSuccess = null;
      state.deleteOrgBudgetScheduledReportError = null;
    },
    deleteOrgBudgetScheduledReportSuccess: (state, action) => {
      state.isDeletingOrgBudgetScheduledReport = false;
      state.deleteOrgBudgetScheduledReportSuccess = action.payload;
      state.deleteOrgBudgetScheduledReportError = null;
    },
    deleteOrgBudgetScheduledReportFailure: (state, action) => {
      state.isDeletingOrgBudgetScheduledReport = false;
      state.deleteOrgBudgetScheduledReportError = action.payload;
    },

    // Budget Group Alerts
    getBudgetGroupAlerts: (state) => {
      state.isFetchingBudgetGroupAlerts = true;
      state.fetchBudgetGroupAlertsError = null;
    },
    getBudgetGroupAlertsSuccess: (state, action) => {
      state.isFetchingBudgetGroupAlerts = false;
      state.budgetGroupAlerts = action.payload;
      state.fetchBudgetGroupAlertsError = null;
    },
    getBudgetGroupAlertsFailure: (state, action) => {
      state.isFetchingBudgetGroupAlerts = false;
      state.budgetGroupAlerts = [];
      state.fetchBudgetGroupAlertsError = action.payload;
    },

    createBudgetGroupAlert: (state) => {
      state.isCreatingBudgetGroupAlert = true;
      state.createBudgetGroupAlertSuccess = null;
      state.createBudgetGroupAlertError = null;
    },
    createBudgetGroupAlertSuccess: (state, action) => {
      state.isCreatingBudgetGroupAlert = false;
      state.createBudgetGroupAlertSuccess = action.payload;
      state.createBudgetGroupAlertError = null;
    },
    createBudgetGroupAlertFailure: (state, action) => {
      state.isCreatingBudgetGroupAlert = false;
      state.createBudgetGroupAlertError = action.payload;
    },

    updateBudgetGroupAlert: (state) => {
      state.isUpdatingBudgetGroupAlert = true;
      state.updateBudgetGroupAlertSuccess = null;
      state.updateBudgetGroupAlertError = null;
    },
    updateBudgetGroupAlertSuccess: (state, action) => {
      state.isUpdatingBudgetGroupAlert = false;
      state.updateBudgetGroupAlertSuccess = action.payload;
      state.updateBudgetGroupAlertError = null;
    },
    updateBudgetGroupAlertFailure: (state, action) => {
      state.isUpdatingBudgetGroupAlert = false;
      state.updateBudgetGroupAlertError = action.payload;
    },

    deleteBudgetGroupAlert: (state) => {
      state.isDeletingBudgetGroupAlert = true;
      state.deleteBudgetGroupAlertSuccess = null;
      state.deleteBudgetGroupAlertError = null;
    },
    deleteBudgetGroupAlertSuccess: (state, action) => {
      state.isDeletingBudgetGroupAlert = false;
      state.deleteBudgetGroupAlertSuccess = action.payload;
      state.deleteBudgetGroupAlertError = null;
    },
    deleteBudgetGroupAlertFailure: (state, action) => {
      state.isDeletingBudgetGroupAlert = false;
      state.deleteBudgetGroupAlertError = action.payload;
    },

    // Budget Group Scheduled Reports
    getBudgetGroupScheduledReports: (state) => {
      state.isFetchingBudgetGroupScheduledReports = true;
      state.fetchBudgetGroupScheduledReportsError = null;
    },
    getBudgetGroupScheduledReportsSuccess: (state, action) => {
      state.isFetchingBudgetGroupScheduledReports = false;
      state.budgetGroupScheduledReports = action.payload;
      state.fetchBudgetGroupScheduledReportsError = null;
    },
    getBudgetGroupScheduledReportsFailure: (state, action) => {
      state.isFetchingBudgetGroupScheduledReports = false;
      state.fetchBudgetGroupScheduledReportsError = action.payload;
    },

    createBudgetGroupScheduledReport: (state) => {
      state.isCreatingBudgetGroupScheduledReport = true;
      state.createBudgetGroupScheduledReportSuccess = null;
      state.createBudgetGroupScheduledReportError = null;
    },
    createBudgetGroupScheduledReportSuccess: (state, action) => {
      state.isCreatingBudgetGroupScheduledReport = false;
      state.createBudgetGroupScheduledReportSuccess = action.payload;
      state.createBudgetGroupScheduledReportError = null;
    },
    createBudgetGroupScheduledReportFailure: (state, action) => {
      state.isCreatingBudgetGroupScheduledReport = false;
      state.createBudgetGroupScheduledReportError = action.payload;
    },

    updateBudgetGroupScheduledReport: (state) => {
      state.isUpdatingBudgetGroupScheduledReport = true;
      state.updateBudgetGroupScheduledReportSuccess = null;
      state.updateBudgetGroupScheduledReportError = null;
    },
    updateBudgetGroupScheduledReportSuccess: (state, action) => {
      state.isUpdatingBudgetGroupScheduledReport = false;
      state.updateBudgetGroupScheduledReportSuccess = action.payload;
      state.updateBudgetGroupScheduledReportError = null;
    },
    updateBudgetGroupScheduledReportFailure: (state, action) => {
      state.isUpdatingBudgetGroupScheduledReport = false;
      state.updateBudgetGroupScheduledReportError = action.payload;
    },

    deleteBudgetGroupScheduledReport: (state) => {
      state.isDeletingBudgetGroupScheduledReport = true;
      state.deleteBudgetGroupScheduledReportSuccess = null;
      state.deleteBudgetGroupScheduledReportError = null;
    },
    deleteBudgetGroupScheduledReportSuccess: (state, action) => {
      state.isDeletingBudgetGroupScheduledReport = false;
      state.deleteBudgetGroupScheduledReportSuccess = action.payload;
      state.deleteBudgetGroupScheduledReportError = null;
    },
    deleteBudgetGroupScheduledReportFailure: (state, action) => {
      state.isDeletingBudgetGroupScheduledReport = false;
      state.deleteBudgetGroupScheduledReportError = action.payload;
    },

    // Clear messages
    clearBudgetMessages: (state) => {
      state.createBudgetRecordSuccess = null;
      state.createBudgetRecordError = null;
      state.updateBudgetRecordSuccess = null;
      state.updateBudgetRecordError = null;
      state.deleteBudgetRecordSuccess = null;
      state.deleteBudgetRecordError = null;
      state.fetchResourceConfigsError = null;
      state.createOrgBudgetSuccess = null;
      state.createOrgBudgetError = null;
      state.updateOrgBudgetSuccess = null;
      state.updateOrgBudgetError = null;
      state.deleteOrgBudgetSuccess = null;
      state.deleteOrgBudgetError = null;
      state.createOrgBudgetAlertSuccess = null;
      state.createOrgBudgetAlertError = null;
      state.updateOrgBudgetAlertSuccess = null;
      state.updateOrgBudgetAlertError = null;
      state.deleteOrgBudgetAlertSuccess = null;
      state.deleteOrgBudgetAlertError = null;

      // Organization Budget Scheduled Reports messages
      state.createOrgBudgetScheduledReportSuccess = null;
      state.createOrgBudgetScheduledReportError = null;
      state.updateOrgBudgetScheduledReportSuccess = null;
      state.updateOrgBudgetScheduledReportError = null;
      state.deleteOrgBudgetScheduledReportSuccess = null;
      state.deleteOrgBudgetScheduledReportError = null;
      state.createBudgetGroupAlertSuccess = null;
      state.createBudgetGroupAlertError = null;
      state.updateBudgetGroupAlertSuccess = null;
      state.updateBudgetGroupAlertError = null;
      state.deleteBudgetGroupAlertSuccess = null;
      state.deleteBudgetGroupAlertError = null;

      // Budget Group Scheduled Reports messages
      state.createBudgetGroupScheduledReportSuccess = null;
      state.createBudgetGroupScheduledReportError = null;
      state.updateBudgetGroupScheduledReportSuccess = null;
      state.updateBudgetGroupScheduledReportError = null;
      state.deleteBudgetGroupScheduledReportSuccess = null;
      state.deleteBudgetGroupScheduledReportError = null;
    },

    // Clear entire budget state
    clearBudgetState: (state) => {
      return initialState;
    },
  },
});

export const {
  getBudgetRecords,
  getBudgetRecordsSuccess,
  getBudgetRecordsFailure,
  getResourceConfigs,
  getResourceConfigsSuccess,
  getResourceConfigsFailure,
  createBudgetRecord,
  createBudgetRecordSuccess,
  createBudgetRecordFailure,
  updateBudgetRecord,
  updateBudgetRecordSuccess,
  updateBudgetRecordFailure,
  deleteBudgetRecord,
  deleteBudgetRecordSuccess,
  deleteBudgetRecordFailure,
  getOrgBudgets,
  getOrgBudgetsSuccess,
  getOrgBudgetsFailure,
  createOrgBudget,
  createOrgBudgetSuccess,
  createOrgBudgetFailure,
  updateOrgBudget,
  updateOrgBudgetSuccess,
  updateOrgBudgetFailure,
  deleteOrgBudget,
  deleteOrgBudgetSuccess,
  deleteOrgBudgetFailure,
  getBudgetCharts,
  getBudgetChartsSuccess,
  getBudgetChartsFailure,
  getBudgetHistory,
  getBudgetHistorySuccess,
  getBudgetHistoryFailure,
  getOrgBudgetHistory,
  getOrgBudgetHistorySuccess,
  getOrgBudgetHistoryFailure,
  getOrgBudgetAlerts,
  getOrgBudgetAlertsSuccess,
  getOrgBudgetAlertsFailure,
  createOrgBudgetAlert,
  createOrgBudgetAlertSuccess,
  createOrgBudgetAlertFailure,
  updateOrgBudgetAlert,
  updateOrgBudgetAlertSuccess,
  updateOrgBudgetAlertFailure,
  deleteOrgBudgetAlert,
  deleteOrgBudgetAlertSuccess,
  deleteOrgBudgetAlertFailure,
  getOrgBudgetScheduledReports,
  getOrgBudgetScheduledReportsSuccess,
  getOrgBudgetScheduledReportsFailure,
  createOrgBudgetScheduledReport,
  createOrgBudgetScheduledReportSuccess,
  createOrgBudgetScheduledReportFailure,
  updateOrgBudgetScheduledReport,
  updateOrgBudgetScheduledReportSuccess,
  updateOrgBudgetScheduledReportFailure,
  deleteOrgBudgetScheduledReport,
  deleteOrgBudgetScheduledReportSuccess,
  deleteOrgBudgetScheduledReportFailure,
  getBudgetGroupAlerts,
  getBudgetGroupAlertsSuccess,
  getBudgetGroupAlertsFailure,
  createBudgetGroupAlert,
  createBudgetGroupAlertSuccess,
  createBudgetGroupAlertFailure,
  updateBudgetGroupAlert,
  updateBudgetGroupAlertSuccess,
  updateBudgetGroupAlertFailure,
  deleteBudgetGroupAlert,
  deleteBudgetGroupAlertSuccess,
  deleteBudgetGroupAlertFailure,
  getBudgetGroupScheduledReports,
  getBudgetGroupScheduledReportsSuccess,
  getBudgetGroupScheduledReportsFailure,
  createBudgetGroupScheduledReport,
  createBudgetGroupScheduledReportSuccess,
  createBudgetGroupScheduledReportFailure,
  updateBudgetGroupScheduledReport,
  updateBudgetGroupScheduledReportSuccess,
  updateBudgetGroupScheduledReportFailure,
  deleteBudgetGroupScheduledReport,
  deleteBudgetGroupScheduledReportSuccess,
  deleteBudgetGroupScheduledReportFailure,
  clearBudgetMessages,
  clearBudgetState,
} = budgetSlice.actions;

export default budgetSlice.reducer;
